import { useTranslation } from "react-i18next";
import type { JSX } from "react";
import { memo, useCallback } from "react";

import { Field } from "../field";

import type { IFastSelectProps } from "./types";
import { dropdownIcons } from "./icon";

import { Select } from "@/components/formControls";
import type { IQueryVariablesType } from "@/api/dropdowns";
import { useOptions } from "@/api/dropdowns/getOptions";
import type { IOption } from "@/interfaces";
import type { ID } from "@/constants";
import { useReadOnly } from "@/hooks";
import { checkFormikEqual } from "@utils";

export const FastSelect = memo(
  function FastSelect<T>(props: IFastSelectProps<T>) {
    const {
      formik,
      name,
      type,
      icon,
      title,
      placeholder,
      readOnly: propsReadOnly,
      dependencies = [],
      disabled,
      onChange,
      onClear,
    } = props;
    const { t } = useTranslation("form");
    const readOnly = useReadOnly(propsReadOnly);
    const defaultIcon = dropdownIcons[type];
    const queryVariables: IQueryVariablesType =
      type === "familyMember"
        ? {
            optionType: "familyMember",
            params: { user_id: props.userId },
          }
        : {
            optionType: type,
            params: undefined,
          };

    const {
      data,
      isFetching: isLoading,
      isError,
    } = useOptions({
      variables: queryVariables,
      enabled: !readOnly,
      staleTime: type === "familyMember" ? undefined : Infinity,
      cacheTime: type === "familyMember" ? undefined : Infinity,
    });
    const options = data ? data.data : [];
    const _title = title || type;
    const { values, setFieldValue, errors, touched, handleBlur } = formik;

    const handleChange = useCallback(
      (value: IOption<ID>) => {
        setFieldValue(name, value);
        onChange?.(value);
        if (dependencies) {
          for (const dependency of dependencies) {
            setFieldValue(dependency, { key: "", value: "" });
          }
          onClear?.([name, ...dependencies]);
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [onChange],
    );

    return (
      <Field
        name={name}
        title={_title}
        formik={props.formik}
        icon={icon || defaultIcon}
        isRequired={props.isRequired && !readOnly}
      >
        <Select
          isLoading={isLoading && !disabled}
          isError={isError}
          placeholder={placeholder || t(_title)}
          id={name.toString()}
          onChange={handleChange}
          onBlur={handleBlur}
          value={values[name] as IOption}
          options={options}
          validation={touched[name] && errors[name] ? "error" : undefined}
          readOnly={readOnly}
          disabled={disabled}
        />
      </Field>
    );
  },
  (prevProps, nextProps) => checkFormikEqual(nextProps, prevProps),
) as unknown as <T>(props: IFastSelectProps<T>) => JSX.Element;
